/**
 * Vibrational Lensing Visualization
 * Visualizes the equation L_f = L_i * (1 + R_v) from the Theory of Sovereign Reflectivity
 */

// Main p5.js sketch
const vibrationalLensingSketch = (p) => {
    // Configuration
    const config = {
        colors: {
            primary: [44, 62, 80], // #2C3E50 (Deep indigo)
            secondary: [142, 68, 173], // #8E44AD (Cosmic purple)
            accent: [241, 196, 15], // #F1C40F (Gold)
            background: [18, 18, 18], // #121212 (Near-black)
            text: [245, 245, 245], // #F5F5F5 (Off-white)
            lowResonance: [231, 76, 60], // #E74C3C (Red)
            highResonance: [46, 204, 113], // #2ECC71 (Green)
        },
        showComparison: false,
    };

    // Variables for the equation
    let nonphysicalMass = 1.0; // Nonphysical Mass (M_np)
    let consciousnessCurrent = 1.0; // Consciousness Current (C_np)
    let proportionalityConstant = 1.0; // Proportionality Constant (k)
    let thetaNp = 1.0; // Vibrational Lensing Angle (θ_np) (calculated)
    
    // Additional variables needed for visualization
    let initialLight = 1.0;
    let vibResonance = 0.5;
    let finalLight = 1.5; // initialLight * (1 + vibResonance)

    // UI elements
    let nonphysicalMassSlider, consciousnessCurrentSlider, kSlider;
    let nonphysicalMassValue, consciousnessCurrentValue, kValue, thetaNpValue;
    let resetButton, toggleComparisonButton;

    // Animation variables
    let time = 0;
    let waves = [];
    let particles = [];

    // Setup function
    p.setup = function () {
        // Create canvas
        const canvas = p.createCanvas(
            p.windowWidth > 800 ? 800 : p.windowWidth - 40,
            500,
        );
        const canvasContainer = document.getElementById(
            "vibrational-lensing-canvas",
        );
        if (canvasContainer) {
            canvas.parent("vibrational-lensing-canvas");
        } else {
            console.warn(
                "Vibrational lensing canvas container not found, visualization may not display correctly",
            );
        }

        // Initialize UI elements from DOM
        nonphysicalMassSlider = document.getElementById("m-np-slider");
        consciousnessCurrentSlider = document.getElementById("c-np-slider");
        kSlider = document.getElementById("k-slider");
        nonphysicalMassValue = document.getElementById("m-np-value");
        consciousnessCurrentValue = document.getElementById("c-np-value");
        kValue = document.getElementById("k-value");
        thetaNpValue = document.getElementById("theta-np-value");
        resetButton = document.getElementById("reset-button");
        toggleComparisonButton = document.getElementById("toggle-comparison");

        // Add event listeners
        if (nonphysicalMassSlider)
            nonphysicalMassSlider.addEventListener("input", updateNonphysicalMass);
        if (consciousnessCurrentSlider)
            consciousnessCurrentSlider.addEventListener("input", updateConsciousnessCurrent);
        if (kSlider)
            kSlider.addEventListener("input", updateProportionalityConstant);
        if (resetButton) resetButton.addEventListener("click", resetValues);
        if (toggleComparisonButton)
            toggleComparisonButton.addEventListener("click", toggleComparison);

        // Initialize waves
        createWaves();

        // Initialize particles
        createParticles();

        // Calculate initial final light
        calculateFinalLight();
    };

    // Draw function
    p.draw = function () {
        p.background(config.colors.background);

        if (config.showComparison) {
            drawComparisonView();
        } else {
            drawStandardView();
        }

        // Update animation time
        time += 0.02;
    };

    // Create wave objects for visualization
    function createWaves() {
        waves = [
            {
                amplitude: 20,
                frequency: 0.02,
                phase: 0,
                color: config.colors.lowResonance,
            },
            {
                amplitude: 20,
                frequency: 0.05,
                phase: p.PI / 4,
                color: config.colors.secondary,
            },
            {
                amplitude: 20,
                frequency: 0.03,
                phase: p.PI / 2,
                color: config.colors.highResonance,
            },
        ];
    }

    // Create particles for light visualization
    function createParticles() {
        particles = [];
        const particleCount = 100;

        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: p.random(p.width),
                y: p.random(p.height),
                size: p.random(2, 6),
                speed: p.random(1, 3),
                angle: p.random(p.TWO_PI),
                color: p.lerpColor(
                    p.color(config.colors.lowResonance),
                    p.color(config.colors.highResonance),
                    p.random(),
                ),
            });
        }
    }

    // Update nonphysical mass from slider
    function updateNonphysicalMass() {
        if (!nonphysicalMassSlider) return;

        nonphysicalMass = parseFloat(nonphysicalMassSlider.value);
        if (nonphysicalMassValue) {
            nonphysicalMassValue.textContent = nonphysicalMass.toFixed(1);
        }
        calculateThetaNp();
    }

    // Update consciousness current from slider
    function updateConsciousnessCurrent() {
        if (!consciousnessCurrentSlider) return;

        consciousnessCurrent = parseFloat(consciousnessCurrentSlider.value);
        if (consciousnessCurrentValue) {
            consciousnessCurrentValue.textContent = consciousnessCurrent.toFixed(1);
        }
        calculateThetaNp();
    }

    // Update proportionality constant from slider
    function updateProportionalityConstant() {
        if (!kSlider) return;

        proportionalityConstant = parseFloat(kSlider.value);
        if (kValue) {
            kValue.textContent = proportionalityConstant.toFixed(1);
        }
        calculateThetaNp();
    }

    // Calculate theta_np based on θ_np = (k · M_np)/C_np²
    function calculateThetaNp() {
        thetaNp = (proportionalityConstant * nonphysicalMass) / (consciousnessCurrent * consciousnessCurrent);
        if (thetaNpValue) {
            thetaNpValue.textContent = thetaNp.toFixed(2) + " rad";
        }
        
        // Calculate vibrational resonance and final light values for visualization
        calculateFinalLight();
    }
    
    // Calculate final light based on the resonance formula L_f = L_i * (1 + R_v)
    function calculateFinalLight() {
        // Calculate vibrational resonance based on the equation parameters
        vibResonance = thetaNp / 10; // Scale thetaNp to a reasonable resonance value
        if (vibResonance > 1) vibResonance = 1; // Cap at 1 for visualization purposes
        
        // Initial light is constant
        initialLight = 1.0;
        
        // Calculate final light using the formula L_f = L_i * (1 + R_v)
        finalLight = initialLight * (1 + vibResonance);
    }

    // Reset all values to defaults
    function resetValues() {
        if (!nonphysicalMassSlider || !consciousnessCurrentSlider || !kSlider) return;

        nonphysicalMassSlider.value = 1.0;
        consciousnessCurrentSlider.value = 1.0;
        kSlider.value = 1.0;
        updateNonphysicalMass();
        updateConsciousnessCurrent();
        updateProportionalityConstant();
    }

    // Toggle comparison view
    function toggleComparison() {
        config.showComparison = !config.showComparison;
        if (toggleComparisonButton) {
            toggleComparisonButton.textContent = config.showComparison
                ? "Hide Comparison"
                : "Toggle Comparison";
        }
    }

    // Draw standard visualization view
    function drawStandardView() {
        // Draw background grid
        drawGrid();

        // Draw wave visualization
        drawWaves();

        // Draw light particles
        drawLightParticles();

        // Draw lens
        drawLens();

        // Draw labels and equation
        drawLabels();
    }

    // Draw wave visualization
    function drawWaves() {
        const centerY = p.height / 2;
        const waveHeight = 100;

        // Draw baseline
        p.stroke(
            config.colors.text[0],
            config.colors.text[1],
            config.colors.text[2],
            100,
        );
        p.strokeWeight(1);
        p.line(0, centerY, p.width, centerY);

        // Draw waves
        for (let i = 0; i < waves.length; i++) {
            const wave = waves[i];
            const amplitude = wave.amplitude * (i === 1 ? vibResonance * 2 : 1);

            p.stroke(wave.color);
            p.strokeWeight(2);
            p.noFill();

            p.beginShape();
            for (let x = 0; x < p.width; x += 5) {
                const y =
                    centerY +
                    Math.sin(x * wave.frequency + time + wave.phase) *
                        amplitude;
                p.vertex(x, y);
            }
            p.endShape();
        }

        // Draw combined wave
        p.stroke(config.colors.accent);
        p.strokeWeight(3);
        p.noFill();

        p.beginShape();
        for (let x = 0; x < p.width; x += 5) {
            let y = centerY;
            for (let i = 0; i < waves.length; i++) {
                const wave = waves[i];
                const amplitude =
                    wave.amplitude * (i === 1 ? vibResonance * 2 : 1);
                y +=
                    (Math.sin(x * wave.frequency + time + wave.phase) *
                        amplitude) /
                    waves.length;
            }
            p.vertex(x, y);
        }
        p.endShape();
    }

    // Draw light particles
    function drawLightParticles() {
        // Check if particles array is initialized
        if (!particles || particles.length === 0) {
            console.warn(
                "Particles array not initialized, recreating particles",
            );
            createParticles();
            return;
        }

        const lensX = p.width / 2;
        const lensWidth = 20;
        const particleCount = Math.floor(50 * initialLight);

        for (let i = 0; i < particleCount; i++) {
            // Make sure we don't exceed the particles array length
            if (i >= particles.length) break;

            const particle = particles[i];

            // Skip if particle is undefined
            if (!particle) continue;

            // Update position
            particle.x += Math.cos(particle.angle) * particle.speed;
            particle.y += Math.sin(particle.angle) * particle.speed;

            // Wrap around edges
            if (particle.x < 0) particle.x = p.width;
            if (particle.x > p.width) particle.x = 0;
            if (particle.y < 0) particle.y = p.height;
            if (particle.y > p.height) particle.y = 0;

            // Apply lens effect
            if (Math.abs(particle.x - lensX) < lensWidth) {
                // Change direction based on resonance
                particle.angle = p.lerp(
                    particle.angle,
                    p.random(p.TWO_PI),
                    vibResonance * 0.1,
                );

                // Change speed based on resonance
                particle.speed = p.lerp(
                    particle.speed,
                    p.random(1, 3) * (1 + vibResonance),
                    0.1,
                );

                // Change color based on resonance
                particle.color = p.lerpColor(
                    p.color(config.colors.lowResonance),
                    p.color(config.colors.highResonance),
                    vibResonance,
                );

                // Change size based on resonance
                particle.size = p.lerp(
                    particle.size,
                    p.random(2, 6) * (1 + vibResonance),
                    0.1,
                );
            }

            // Draw particle
            p.noStroke();
            p.fill(particle.color);
            p.ellipse(particle.x, particle.y, particle.size);
        }
    }

    // Draw lens
    function drawLens() {
        const lensX = p.width / 2;
        const lensHeight = p.height * 0.8;
        const lensWidth = 20;

        // Draw lens body
        p.noStroke();
        p.fill(
            config.colors.secondary[0],
            config.colors.secondary[1],
            config.colors.secondary[2],
            100,
        );
        p.rect(
            lensX - lensWidth / 2,
            p.height / 2 - lensHeight / 2,
            lensWidth,
            lensHeight,
        );

        // Draw lens border
        p.stroke(config.colors.secondary);
        p.strokeWeight(2);
        p.noFill();
        p.rect(
            lensX - lensWidth / 2,
            p.height / 2 - lensHeight / 2,
            lensWidth,
            lensHeight,
        );

        // Draw resonance indicator
        const indicatorHeight = lensHeight * vibResonance;
        p.noStroke();
        p.fill(
            config.colors.accent[0],
            config.colors.accent[1],
            config.colors.accent[2],
            150,
        );
        p.rect(
            lensX - lensWidth / 2,
            p.height / 2 + lensHeight / 2 - indicatorHeight,
            lensWidth,
            indicatorHeight,
        );

        // Draw lens label
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.text("Resonance Lens", lensX, p.height / 2 - lensHeight / 2 - 20);
        p.textSize(12);
        p.text(
            `R_v = ${vibResonance.toFixed(2)}`,
            lensX,
            p.height / 2 + lensHeight / 2 + 20,
        );
    }

    // Draw background grid
    function drawGrid() {
        p.stroke(
            config.colors.primary[0],
            config.colors.primary[1],
            config.colors.primary[2],
            50,
        );
        p.strokeWeight(1);

        const gridSize = 30;
        for (let x = 0; x < p.width; x += gridSize) {
            p.line(x, 0, x, p.height);
        }

        for (let y = 0; y < p.height; y += gridSize) {
            p.line(0, y, p.width, y);
        }
    }

    // Draw comparison view (low vs high resonance)
    function drawComparisonView() {
        // Draw dividing line
        p.stroke(config.colors.accent);
        p.strokeWeight(2);
        p.line(p.width / 2, 0, p.width / 2, p.height);

        // Draw titles
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(16);
        p.text("Low Resonance (R_v = 0.1)", p.width * 0.25, 30);
        p.text("High Resonance (R_v = 0.9)", p.width * 0.75, 30);

        // Draw equations
        p.fill(config.colors.accent);
        p.textSize(20);
        p.text(`L_f = 1.0 × (1 + 0.1) = 1.1`, p.width * 0.25, 60);
        p.text(`L_f = 1.0 × (1 + 0.9) = 1.9`, p.width * 0.75, 60);

        // Draw low resonance side (left)
        drawResonanceScenario(p.width * 0.25, 0.1);

        // Draw high resonance side (right)
        drawResonanceScenario(p.width * 0.75, 0.9);

        // Draw explanation
        p.fill(config.colors.text);
        p.textSize(14);
        p.textAlign(p.CENTER, p.CENTER);
        p.text("Minimal light amplification", p.width * 0.25, p.height - 40);
        p.text(
            "Significant light amplification",
            p.width * 0.75,
            p.height - 40,
        );
    }

    // Draw a resonance scenario for the comparison view
    function drawResonanceScenario(centerX, resonanceValue) {
        const centerY = p.height / 2;
        const radius = 100;

        // Draw resonance circle
        p.noFill();
        p.stroke(
            p.lerpColor(
                p.color(config.colors.lowResonance),
                p.color(config.colors.highResonance),
                resonanceValue,
            ),
        );
        p.strokeWeight(3);
        p.ellipse(centerX, centerY, radius * 2);

        // Draw resonance waves
        for (let i = 0; i < 3; i++) {
            const waveRadius = radius * (0.6 + i * 0.2);
            p.noFill();
            p.stroke(
                config.colors.secondary[0],
                config.colors.secondary[1],
                config.colors.secondary[2],
                100 - i * 30,
            );
            p.strokeWeight(2);

            p.beginShape();
            for (let angle = 0; angle < p.TWO_PI; angle += 0.1) {
                const r =
                    waveRadius +
                    Math.sin(angle * 5 + time) * (10 * resonanceValue);
                const x = centerX + Math.cos(angle) * r;
                const y = centerY + Math.sin(angle) * r;
                p.vertex(x, y);
            }
            p.endShape(p.CLOSE);
        }

        // Draw light particles
        const particleCount = 30;
        for (let i = 0; i < particleCount; i++) {
            const angle = i * (p.TWO_PI / particleCount);
            const distance = p.random(0, radius * (1 + resonanceValue * 0.5));
            const x =
                centerX + Math.cos(angle + time * resonanceValue) * distance;
            const y =
                centerY + Math.sin(angle + time * resonanceValue) * distance;

            p.noStroke();
            p.fill(
                p.lerpColor(
                    p.color(config.colors.lowResonance),
                    p.color(config.colors.highResonance),
                    resonanceValue,
                ),
            );
            p.ellipse(x, y, 4 + resonanceValue * 4);
        }

        // Draw light intensity indicator
        const initialIntensity = 1.0;
        const finalIntensity = initialIntensity * (1 + resonanceValue);

        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.text(
            `Initial Light: ${initialIntensity.toFixed(1)}`,
            centerX,
            centerY - radius - 40,
        );
        p.text(
            `Final Light: ${finalIntensity.toFixed(2)}`,
            centerX,
            centerY + radius + 40,
        );

        // Draw arrow
        p.stroke(config.colors.accent);
        p.strokeWeight(2);
        p.fill(config.colors.accent);
        drawArrow(
            centerX,
            centerY - radius - 20,
            centerX,
            centerY + radius + 20,
        );
    }

    // Helper function to draw arrows
    function drawArrow(x1, y1, x2, y2) {
        p.line(x1, y1, x2, y2);

        const angle = p.atan2(y2 - y1, x2 - x1);
        const arrowSize = 10;

        p.push();
        p.translate(x2, y2);
        p.rotate(angle);
        p.triangle(-arrowSize, arrowSize / 2, -arrowSize, -arrowSize / 2, 0, 0);
        p.pop();
    }

    // Draw labels and equation
    function drawLabels() {
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);

        // Draw equation at the top
        p.textSize(24);
        p.fill(config.colors.accent);
        p.text(`L_f = L_i × (1 + R_v)`, p.width / 2, 30);

        // Draw current values
        p.textSize(16);
        p.fill(config.colors.text);
        p.text(
            `L_i = ${initialLight.toFixed(1)}   R_v = ${vibResonance.toFixed(2)}   L_f = ${finalLight.toFixed(2)}`,
            p.width / 2,
            60,
        );

        // Draw input and output labels
        p.textSize(14);
        p.text("Input Light", p.width / 4, p.height - 20);
        p.text("Output Light", (p.width * 3) / 4, p.height - 20);

        // Draw arrows
        p.stroke(config.colors.text);
        p.strokeWeight(2);
        p.fill(config.colors.text);
        drawArrow(p.width / 4, p.height - 40, p.width / 2 - 20, p.height / 2);
        drawArrow(
            p.width / 2 + 20,
            p.height / 2,
            (p.width * 3) / 4,
            p.height - 40,
        );
    }

    // Handle window resize
    p.windowResized = function () {
        p.resizeCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
    };
};

// Initialize the p5.js sketch
new p5(vibrationalLensingSketch);
